import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import icons from './shared/Icons';
import Section from './shared/Section';
import SectionHeading from './shared/SectionHeading';

const steps = [
  {
    num: '01',
    title: 'Connect Your Data',
    desc: 'Plug in any data source databases, APIs, spreadsheets, IoT streams with our 200+ pre-built connectors.',
    icon: 'globe',
    color: '#08CB00',
    image: process.env.PUBLIC_URL + '/process_result.webp',
    demo: 'Watch how 200+ connectors unify siloed data into a single, query-ready lake in under 60 seconds.',
  },
  {
    num: '02',
    title: 'AI Processes & Learns',
    desc: 'Our neural engine automatically cleanses, structures, and analyzes your data, learning patterns unique to your domain.',
    icon: 'brain',
    color: '#54E346',
    image: process.env.PUBLIC_URL + '/process (2)_result.webp',
    demo: 'See our neural engine detect anomalies, fill missing values, and build feature embeddings fully automated.',
  },
  {
    num: '03',
    title: 'Generate Insights',
    desc: 'Receive actionable intelligence through interactive dashboards, automated reports, and natural language summaries.',
    icon: 'chart',
    color: '#08CB00',
    image: process.env.PUBLIC_URL + '/process (3)_result.webp',
    demo: 'Explore interactive dashboards that turn billions of data points into clear, visual narratives instantly.',
  },
  {
    num: '04',
    title: 'Act & Optimize',
    desc: 'Implement AI-driven recommendations, monitor outcomes in real-time, and continuously refine your strategy.',
    icon: 'zap',
    color: '#54E346',
    image: process.env.PUBLIC_URL + '/process (4)_result.webp',
    demo: 'Discover how AI-driven recommendations auto-deploy, track KPIs, and self-optimize over time.',
  },
];

/* ── Curved arrow SVG between nodes ── */
const CurvedArrow = ({ index, inView }) => (
  <svg
    className="hidden lg:block absolute top-1/2 -translate-y-1/2"
    style={{ left: '100%', width: 80, height: 60, marginLeft: -8 }}
    viewBox="0 0 80 60"
    fill="none"
    aria-hidden="true"
  >
    <motion.path
      d="M4 30 C 20 8, 60 8, 76 30"
      stroke="#54E346"
      strokeWidth="2"
      strokeDasharray="4 4"
      fill="none"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={inView ? { pathLength: 1, opacity: 1 } : {}}
      transition={{ duration: 0.8, delay: 0.3 + index * 0.4, ease: 'easeInOut' }}
    />
    <motion.polygon
      points="70,26 78,30 70,34"
      fill="#54E346"
      initial={{ opacity: 0 }}
      animate={inView ? { opacity: 1 } : {}}
      transition={{ duration: 0.3, delay: 0.9 + index * 0.4 }}
    />
  </svg>
);

/* ── Description popup ── */
const DemoModal = ({ step, onClose }) => (
  <motion.div
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
    <motion.div
      className="relative glass-card rounded-2xl p-8 max-w-lg w-full z-10"
      initial={{ scale: 0.85, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.85, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        aria-label="Close modal"
      >
        {icons.close}
      </button>

      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center text-primary overflow-hidden">
          {step.image ? (
            <div className="w-full h-full bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url("${encodeURI(step.image)}")` }} />
          ) : (
            icons[step.icon]
          )}
        </div>
        <div>
          <span className="text-xs text-primary/60 font-mono uppercase tracking-wider">Step {step.num}</span>
          <h3 className="text-xl font-bold">{step.title}</h3>
        </div>
      </div>

      {/* Accent divider */}
      <div className="h-px w-full bg-gradient-to-r from-primary/40 via-primary/10 to-transparent mb-6" />

      {/* Description */}
      <p className="text-gray-300 text-sm leading-relaxed mb-4">{step.desc}</p>

      {/* Detailed info */}
      <div className="rounded-xl bg-dark-card/60 border border-dark-border p-5 mb-6">
        <p className="text-gray-400 text-sm leading-relaxed">{step.demo}</p>
      </div>

      {/* Feature pills */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs">Automated</span>
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs">Real-time</span>
        <span className="px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs">Enterprise-grade</span>
      </div>
    </motion.div>
  </motion.div>
);

/* ── Step node ── */
const StepNode = ({ step, index, onOpen, inView }) => (
  <div className="relative flex-shrink-0 w-60 lg:w-auto">
    <motion.div
      className="flex flex-col items-center text-center group"
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.3 }}
    >
      {/* Circle node */}
        <button
        onClick={() => onOpen(step)}
        className="relative w-24 h-24 rounded-full bg-dark-card border-2 border-primary/20 flex items-center justify-center mb-5 group-hover:border-primary group-hover:shadow-xl group-hover:shadow-primary/15 transition-all duration-500 cursor-pointer overflow-hidden"
        aria-label={`Learn more about step ${step.num}`}
      >
        {step.image ? (
          <div className="w-16 h-16 bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url("${encodeURI(step.image)}")` }} />
        ) : (
          <div className="text-primary group-hover:scale-110 transition-transform duration-300">
            {icons[step.icon]}
          </div>
        )}
        {/* Step number badge */}
        <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-dark border-2 border-primary/40 flex items-center justify-center text-xs font-bold font-mono gradient-text">
          {step.num}
        </span>
        {/* Pulse ring on hover */}
        <span className="absolute inset-0 rounded-full border-2 border-primary/0 group-hover:border-primary/20 group-hover:scale-125 transition-all duration-700 pointer-events-none" />
      </button>

      <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors">{step.title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed max-w-[220px]">{step.desc}</p>
    </motion.div>

    {/* Arrow to next (desktop) */}
    {index < steps.length - 1 && <CurvedArrow index={index} inView={inView} />}
  </div>
);

/* ── Mobile vertical step ── */
const MobileStep = ({ step, index, onOpen }) => (
  <motion.div
    className="relative flex gap-5"
    initial={{ opacity: 0, x: -30 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.15 }}
  >
    {/* Vertical line + node */}
    <div className="flex flex-col items-center">
      <button
        onClick={() => onOpen(step)}
        className="relative w-14 h-14 rounded-full bg-dark-card border-2 border-primary/20 flex items-center justify-center flex-shrink-0 hover:border-primary transition-colors overflow-hidden"
        aria-label={`Learn more about step ${step.num}`}
      >
        {step.image ? (
          <div className="w-full h-full bg-center bg-contain bg-no-repeat" style={{ backgroundImage: `url("${encodeURI(step.image)}")` }} />
        ) : (
          <span className="text-primary scale-75">{icons[step.icon]}</span>
        )}
        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-dark border border-primary/40 flex items-center justify-center text-[10px] font-bold font-mono gradient-text">
          {step.num}
        </span>
      </button>
      {index < steps.length - 1 && (
        <div className="w-px flex-1 border-l-2 border-dashed border-primary/20 mt-2" />
      )}
    </div>

    {/* Content */}
    <div className="pb-10">
      <h3 className="text-base font-bold mb-1">{step.title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed mb-2">{step.desc}</p>
    </div>
  </motion.div>
);

/* ── Main component ── */
const HowItWorks = () => {
  const [modalStep, setModalStep] = useState(null);
  const flowRef = useRef(null);
  const inView = useInView(flowRef, { once: true, margin: '-80px' });

  return (
    <Section id="how-it-works" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag="Process"
          title={<>From Data to Decisions in <span className="gradient-text">Four Steps</span></>}
          desc="A streamlined workflow that collapses months of traditional analytics into minutes."
        />

        {/* ── Desktop horizontal flowchart ── */}
        <div
          ref={flowRef}
          className="hidden lg:flex justify-between items-start gap-8 relative overflow-x-auto pb-4"
        >
          {steps.map((step, i) => (
            <StepNode key={step.num} step={step} index={i} onOpen={setModalStep} inView={inView} />
          ))}
        </div>

        {/* ── Tablet horizontal scroll ── */}
        <div className="hidden sm:flex lg:hidden gap-8 overflow-x-auto snap-x snap-mandatory pb-6 -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
          {steps.map((step, i) => (
            <div key={step.num} className="snap-center">
              <StepNode step={step} index={i} onOpen={setModalStep} inView={true} />
            </div>
          ))}
        </div>

        {/* ── Mobile vertical stack ── */}
        <div className="sm:hidden">
          {steps.map((step, i) => (
            <MobileStep key={step.num} step={step} index={i} onOpen={setModalStep} />
          ))}
        </div>
      </div>

      {/* ── Modal ── */}
      <AnimatePresence>
        {modalStep && <DemoModal step={modalStep} onClose={() => setModalStep(null)} />}
      </AnimatePresence>
    </Section>
  );
};

export default HowItWorks;
